import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
} from "@angular/core";
import { DataService } from "@barthauer/abc/core/data";
import { GUID } from "@barthauer/abc/core";
import { AbcConfigService } from "@barthauer/abc/core/configuration";
import { EMPTY, Observable, Subject } from "rxjs";
import { catchError, map, takeUntil } from "rxjs/operators";
import { GeneratorTranslation } from "../../models/view.model";
import { SharedService } from "../../services/shared.service";
import {
  ChatMessage,
  ChatRequest,
  ChatResponse,
  ChatUser,
  ContextMode,
} from "../../models/generator.model";
import { HttpHeaders } from "@angular/common/http";
import { WebApiService } from "@barthauer/abc/core/authentication";
@Component({
  selector: "expressions-generator",
  templateUrl: "./expression-generator.component.html",
  styleUrls: ["./expression-generator.component.scss"],
  standalone: false,
})
export class ExpressionsGeneratorComponent implements OnDestroy {
  @Input() objectType: string;
  @Input() contextMode: ContextMode = "arena";
  @Input() sessionId: string;
  @Output() onQueryGenerated = new EventEmitter<string>();
  readonly currentUser: ChatUser = { name: sessionStorage.getItem("userName") };
  readonly assistantUser: ChatUser;
  public translation: GeneratorTranslation;
  public chatMessages: ChatMessage[] = [];
  public typingUsers: ChatUser[] = [];
  public queryError: string = "";
  public generating: boolean = false;
  private opathGeneratorWebApi: string;
  private opathGeneratorToken: string;
  private unsubscribe$ = new Subject<void>();
  public lastGeneratedOpath: string;

  constructor(
    private dataService: DataService,
    private sharedService: SharedService,
    private configService: AbcConfigService,
  ) {
    this.opathGeneratorWebApi = "https://ki.barthauer.com/opath/chat";
    this.opathGeneratorToken =
      this.configService.configuration["SpecificInfo"].KiAssistentToken;
    this.translation = this.sharedService.translation.generator;
    this.assistantUser = { name: this.translation.assistantName };
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next(null);
    this.unsubscribe$.complete();
  }

  public onMessageEntered(event: { message: ChatMessage }): void {
    const text = event.message?.text?.trim();
    if (!text || this.generating) return;
    this.generateQuery(text);
  }

  private generateQuery(prompt: string): void {
    this.generating = true;
    this.queryError = "";
    this.typingUsers = [this.assistantUser];

    const body = {
      prompt,
      session_id: this.sessionId,
      selected_contexts: [
        { contextMode: this.contextMode },
        { entity: this.objectType },
      ],
    };

    const options = {
      observe: "body",
      headers: new HttpHeaders({
        Authorization: `Bearer ${this.opathGeneratorToken}`,
        [WebApiService.HTTP_HEADER_AVOID_AUTH_HEADERS]: "true",
      }),
    };

    (
      this.dataService.executeWebApiPOSTMethodWithOptions(
        this.opathGeneratorWebApi,
        body,
        options,
      ) as unknown as Observable<ChatResponse>
    )
      .pipe(
        catchError((err: unknown) => {
          this.handleQueryError(err);
          return EMPTY;
        }),
        takeUntil(this.unsubscribe$),
      )
      .subscribe({
        next: ({ opaths }) => this.handleQueryResult(opaths?.[0] ?? ""),
      });
  }

  private handleQueryResult(opath: string): void {
    this.typingUsers = [];
    this.generating = false;

    if (!opath) {
      this.queryError = this.translation?.noResult;
      return;
    }

    this.lastGeneratedOpath = opath;
    this.chatMessages = [
      ...this.chatMessages,
      {
        id: GUID.newGUID(),
        author: this.assistantUser,
        text: `${opath}`,
        timestamp: new Date(),
      },
    ];
  }

  public applyGeneratedQuery(): void {
    if (this.lastGeneratedOpath) {
      this.onQueryGenerated.emit(this.lastGeneratedOpath);
    }
  }

  public isLastAiAssistantMessage(message: ChatMessage): boolean {
    if (!this.lastGeneratedOpath) return false;
    const assistantMessages = this.chatMessages.filter(
      (m) => m.author?.name === this.assistantUser.name,
    );
    const last = assistantMessages[assistantMessages.length - 1];
    return !!last && last.id === message.id;
  }

  private handleQueryError(err: unknown): void {
    this.typingUsers = [];
    this.generating = false;
    this.queryError = this.translation?.errorMessage;
  }
}
